import { Stack, Text, Group, ThemeIcon } from "@mantine/core"
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react"
import _ from "lodash"
import { useMemo } from "react"

import { ExploreResponses } from "@costory/types/endpoints/explorer"
import { Filters } from "@costory/types/filters"
import { Currency } from "@costory/types/prisma-client"

import { formatNumber } from "@costory/shared/utils/format"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useWaterfallQuery } from "@costory/front/queries/explore"

type KPICardProps = {
  data: ExploreResponses.Waterfall["dataGroupedByValue"]
  currency: Currency
  datePreset?: string | null | undefined
}

const KPICard = ({ data, currency }: KPICardProps) => {
  const totals = useMemo(
    () => ({
      current: _.sumBy(data, (d) => d.periodCost),
      previous: _.sumBy(data, (d) => d.previousPeriodCost),
    }),
    [data],
  )

  const changeInfo = useMemo(() => {
    const ratio =
      totals.previous !== 0
        ? (totals.current - totals.previous) / totals.previous
        : 0
    const absoluteChange = totals.current - totals.previous

    return {
      ratio,
      absoluteChange,
      isIncrease: ratio > 0,
      isDecrease: ratio < 0,
    }
  }, [totals])

  return (
    <Stack gap={12} align="flex-start">
      <Text size="2rem" fw={700}>
        {formatNumber({ value: totals.current, currency })}
      </Text>

      {changeInfo.ratio !== 0 && (
        <Group gap={8} align="center">
          <ThemeIcon
            size="sm"
            radius="xl"
            color={changeInfo.isIncrease ? "red" : "green"}
            variant="light"
          >
            {changeInfo.isIncrease ? (
              <IconArrowUp size={14} />
            ) : (
              <IconArrowDown size={14} />
            )}
          </ThemeIcon>
          <Text
            size="sm"
            c={changeInfo.isIncrease ? "red.7" : "green.7"}
            fw={500}
          >
            {formatNumber({
              value: changeInfo.ratio,
              numberType: "percent",
              maxDigit: 1,
            })}
          </Text>
          <Text size="sm" c="textMuted" fw={500}>
            (
            {formatNumber({
              value: changeInfo.absoluteChange,
              currency,
            })}
            )
          </Text>
        </Group>
      )}
    </Stack>
  )
}

type Props = {
  filters: Filters
}

export const KPICardChart = ({ filters }: Props) => {
  const waterfallQuery = useWaterfallQuery({ ...filters, limit: 4 })

  return (
    <QueryWrapper query={waterfallQuery}>
      {({ data }) => (
        <KPICard
          data={data.dataGroupedByValueWithOthers}
          currency={filters.currency}
          datePreset={filters.datePreset}
        />
      )}
    </QueryWrapper>
  )
}
